import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { api } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';
import type { PosNote } from '../../types';

export function useVisitNotes(
  posId: string,
  visitId: string,
  isOpen: boolean
): { notes: PosNote[]; timelineEmpty: boolean; ready: boolean } {
  const [notes, setNotes] = useState<PosNote[]>([]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    async function fetchNotes() {
      setReady(false);
      try {
        const { data } = await api.get<PosNote[]>(`/pos/${posId}/notes`, {
          params: { visit_id: visitId },
        });
        if (!cancelled) {
          setNotes(Array.isArray(data) ? data : []);
          setFailed(false);
        }
      } catch {
        if (!cancelled) {
          setNotes([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void fetchNotes();
    return () => {
      cancelled = true;
    };
  }, [isOpen, posId, visitId]);

  const timelineEmpty = !ready || failed || notes.length === 0;
  return { notes, timelineEmpty, ready };
}

interface VisitNotesSectionProps {
  notes: PosNote[];
  timelineEmpty: boolean;
  legacyNotes?: string;
}

export const VisitNotesSection: React.FC<VisitNotesSectionProps> = ({
  notes,
  timelineEmpty,
  legacyNotes,
}) => {
  if (!timelineEmpty) {
    return (
      <div className="bg-gray-50 p-4 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={18} className="text-gray-600" />
          <h3 className="font-bold text-gray-900">Notes</h3>
        </div>
        <TimelineList notes={notes} />
      </div>
    );
  }

  if (!legacyNotes) return null;

  return (
    <div className="bg-gray-50 p-4 rounded-xl">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={18} className="text-gray-600" />
        <h3 className="font-bold text-gray-900">Notes</h3>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{legacyNotes}</p>
    </div>
  );
};

function TimelineList({ notes }: { notes: PosNote[] }) {
  return (
    <ul className="space-y-2">
      {notes.map((note) => (
        <li
          key={note.id}
          className="bg-white rounded-lg border border-gray-200 p-3"
        >
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-xs font-bold text-gray-800 truncate">
              {note.user_name || 'Unbekannt'}
            </span>
            <span className="text-[11px] text-gray-500 flex-shrink-0">
              {formatDateTime(note.created_at)}
            </span>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.body}</p>
        </li>
      ))}
    </ul>
  );
}
